'use client';

import { blockKey } from '@hyphengroup/cms-components/calculatedAttributes/blockKey';
import { TableBuilder } from '@hyphengroup/cms-components/cms-blocks/TableBuilder';
import { ComponentTableBuilder } from '@hyphengroup/cms-components/cms-blocks/TableBuilder/types';
import { useMemo } from 'react';
import { getLoanSearchParamsWithDefaultComponentParam } from '../helpers/loanSearchParams';
import { useModuleQuotesPoll } from '../helpers/useModuleQuotesPoll';
import { useClientContext } from '$/modules/context/client';

/**
 * Client wrapper for the CMS TableBuilder block. Injects the block's loan
 * amount/tenure for the CTA (CMS-1921 R1); the table's own quotes and loan
 * calculations reach it through the calculation store under the block's own
 * address, like every other block (CMS-1890).
 *
 * When the SSR snapshot left the quote poll `inprogress`, the wrapper also
 * continues it client-side (`useModuleQuotesPoll`) — each tick lands in the
 * page store and re-renders the table, filling the loading price cells in.
 * Loan calculations need no such thing: they settle server-side in one call.
 */
type TableBuilderWrapperProps = Omit<ComponentTableBuilder, 'loanSearchParams'>;

export const TableBuilderWrapper: React.FC<TableBuilderWrapperProps> = (
  props,
) => {
  const { brand } = useClientContext();

  // Continue an SSR-started quote poll for THIS table, if one is unfinished.
  useModuleQuotesPoll(blockKey(props));

  const isPersonalLoan = props.mode === 'PERSONAL_LOAN';

  // In Personal Loan mode the module's figures are its own: the reader moving
  // the page calculator must not change what this table states, and its apply
  // CTA must carry the same loan the table was calculated from (CMS-1921 R1).
  // So the CTA params come from the authored fields, never from the URL.
  const moduleLoanSearchParams = useMemo(
    () =>
      getLoanSearchParamsWithDefaultComponentParam(brand, undefined, {
        amount: props.loanAmount?.toString(),
        tenure: props.loanTenure?.toString(),
      }),
    [brand, props.loanAmount, props.loanTenure],
  );

  // Other modes put the CTA under no loan vertical, so a loan would go unread.
  const loanSearchParams = isPersonalLoan ? moduleLoanSearchParams : undefined;

  return <TableBuilder {...props} loanSearchParams={loanSearchParams} />;
};
